import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import styled from 'styled-components';
import IsEmpty from 'lodash/isEmpty';

import { useOrderData } from '../../../../hooks/useOrderData';
import Money from 'core/api/Money';
import { ReactComponent as InfoIconOutline } from 'core/assets/images/V2/icons/InfoIconOutline.svg';
import { ReactComponent as CreditedIcon } from 'core/assets/images/V2/icons/CreditedIcon.svg';
import { GET_ELIGIBLE_MEAL_SWAPS } from 'core/api/apollo/queries';
import { ADD_USER_MEAL_SWAP_REQUEST } from 'core/api/apollo/mutations';
import { Box, Flex, Button, Hr, Text } from 'core/ui/atoms';
import { NavigationHeader, MealItem } from 'onramp/ui/components';
import Loading from 'core/ui/components/Loading';
import { isLoading } from 'core/api/loadingHelpers';
import { editWeekMenuPath } from 'onramp/api/navigation';

const SelectMealPage = () => {
  const history = useHistory();
  const { deliveryDayId, componentId } = useParams();
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [showCreditPopover, setShowCreditPopover] = useState(false);
  const { refetchOrders } = useOrderData();

  const variables = { deliveryDayId: deliveryDayId, mealComponentId: componentId };

  const { loading, error, data: { getEligibleMealSwaps = [] } = {} } = useQuery(GET_ELIGIBLE_MEAL_SWAPS, {
    variables,
  });

  useEffect(() => {
    if (getEligibleMealSwaps.length > 0) {
      const defaultMeal = getEligibleMealSwaps.find((meal) =>
        meal.variants.some((variant) => variant.id === meal.variantId)
      );
      if (defaultMeal) {
        setSelectedMeal(defaultMeal);
      }
    }
  }, [getEligibleMealSwaps]);

  const [addUserMealSwapRequest] = useMutation(ADD_USER_MEAL_SWAP_REQUEST, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('Meal swap request added successfully:', data);
      refetchOrders();
      navigateToEditMenu();
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error adding meal swap request:', error);
    },
  });

  if (isLoading(loading, getEligibleMealSwaps, error)) {
    return <Loading />;
  }

  if (!loading && IsEmpty(getEligibleMealSwaps)) {
    return <Text>No meals available.</Text>;
  }

  const isBaseMeal = getEligibleMealSwaps[0]?.id === selectedMeal?.id;

  const isCreditAmount = () => {
    const baseMealAmount = getEligibleMealSwaps[0]?.cost?.amount;
    const costAmount = selectedMeal?.cost?.amount;
    return (baseMealAmount > costAmount) ? true : false;
  };

  const getTotalAmount = () => {
    const baseMealAmount = getEligibleMealSwaps[0]?.cost?.amount;
    const costAmount = selectedMeal?.cost?.amount;

    if (costAmount > baseMealAmount) {
      return {
        __typename: 'Money',
        amount: costAmount - baseMealAmount,
      };
    } else if (baseMealAmount > costAmount) {
      return {
        __typename: 'Money',
        amount: baseMealAmount - costAmount,
      };
    }
  };

  const navigateToEditMenu = () => {
    history.replace(editWeekMenuPath(deliveryDayId));
  };

  const handleConfirmSelection = async () => {
    try {
      await addUserMealSwapRequest({
        variables: {
          input: {
            deliveryDayId: deliveryDayId,
            originalComponentId: getEligibleMealSwaps[0]?.id,
            replacedComponentId: selectedMeal?.id,
            requestType: 'Swap',
            quantity: 1,
          },
        },
      });
      refetchOrders();
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
  };

  const handleMealSelection = (mealItem) => {
    setSelectedMeal(mealItem);
  };

  const RenderMealList = () => {
    return getEligibleMealSwaps.map((item) => (
      <MealBoxContainer key={item.id}>
        <MealItem
          item={item}
          isSelected={selectedMeal?.id === item?.id}
          deliveryDayId={deliveryDayId}
          baseMealItem={getEligibleMealSwaps[0]}
          onSelectedMealChange={() => handleMealSelection(item)}
        />
      </MealBoxContainer>
    ));
  };

  const getIcon = () => isCreditAmount() && <CreditIcon />;

  return (
    <Box>
      <NavigationHeader
        title='Pick Your Swap'
        backNavigate={navigateToEditMenu}
      />
      <Hr />
      <MealListContainer direction='column' justifyContent='center'>
        <RenderMealList />
        {getEligibleMealSwaps.length === 0 && (
          <Text>No meals available.</Text>
        )}
      </MealListContainer>
      {!isBaseMeal &&
        <ConfirmMealButtonContainer direction='column' alignItems='center'>
          <ConfirmButton onClick={handleConfirmSelection}>
            CONFIRM MEAL SELECTION - {getIcon()} {Money(getTotalAmount()).toFormat('$0,0')}
          </ConfirmButton>
          {isCreditAmount() &&
            <InfoContainer>
              <Flex direction='row' justifyContent='center' alignItems='center' gap='5px'>
                <Text>credit amount</Text>
                <ClickableInfoIcon onClick={() => setShowCreditPopover(!showCreditPopover)} />
              </Flex>
              {showCreditPopover && (
                <PopoverContainer>
                  <PopoverContent>
                    <PopoverText>
                      Your credits will be used as a discount on your next order.
                      No expiry. No pressure. Just tasty savings.
                    </PopoverText>
                    <CloseButton onClick={() => setShowCreditPopover(false)}>✕</CloseButton>
                  </PopoverContent>
                </PopoverContainer>
              )}
            </InfoContainer>
          }
        </ConfirmMealButtonContainer>
      }
    </Box>
  );
};

const MealBoxContainer = styled(Box)`
  width: 100%;
`;

const MealListContainer = styled(Flex)`
  padding-left: 15px;
  padding-right: 15px;
  row-gap: 20px;
  margin-bottom: 50px;
  margin-top: 25px;
`;

const CreditIcon = styled(CreditedIcon)`
  margin-bottom: 5px;
`;

const ConfirmMealButtonContainer = styled(Flex)`
  justify-content: center;
  position: fixed;
  width: 100%;
  bottom: 0;
  left: 0;
  padding: 20px;
  background-color: ${(p) => p.theme.colors.creamWhite};
  height: 110px;
`;

const ConfirmButton = styled(Button)`
  padding: 12px 10px;
  border-radius: 4px;
  display: flex;
  width: 95%;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 15px;
  background: ${(p) => p.theme.colors.green.light};
  ${(p) => p.theme.max('sm')`
    margin-bottom: 0;
  `}
`;

const InfoContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ClickableInfoIcon = styled(InfoIconOutline)`
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const PopoverContainer = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 10px;
  z-index: 1000;
`;

const PopoverContent = styled.div`
  background: ${(p) => p.theme.colors.white};
  border: 1px solid ${(p) => p.theme.colors.grays.light};
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 280px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 8px solid transparent;
    border-top-color: ${(p) => p.theme.colors.white};
  }

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 9px solid transparent;
    border-top-color: ${(p) => p.theme.colors.grays.light};
    z-index: -1;
  }
`;

const PopoverText = styled(Text)`
  font-size: 14px;
  line-height: 1.4;
  color: ${(p) => p.theme.colors.black};
  margin: 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: ${(p) => p.theme.colors.grays.medium};
  padding: 2px;
  line-height: 1;

  &:hover {
    color: ${(p) => p.theme.colors.black};
  }
`;

export default SelectMealPage;
